import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar, ArrowUpRight, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CTASection from '../components/CTASection';
import SEO from '../components/SEO';
import { fetchBlogs } from '../lib/api';

export default function InsightsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs()
      .then((data) => setPosts(data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div>
      <SEO
        title="Insights & Welding Guides"
        description="Practical welding tips, electrode guides, and technical articles from Prakash Electrodes Private Limited. Fresh insights published daily."
        path="/insights"
      />
      <Navbar />

      <section className="relative bg-[#0b0b0d] text-white pt-40 pb-24 overflow-hidden noise">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 mb-6">
            <Sparkles className="h-3 w-3 text-amber-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300">
              AI-updated daily
            </span>
          </div>
          <h1 className="font-display text-5xl lg:text-6xl font-semibold leading-[1.05] max-w-3xl">
            Welding wisdom from the shop floor.
          </h1>
          <p className="mt-6 text-white/70 max-w-2xl text-lg leading-relaxed">
            Tips, techniques, and technical guides from four decades of manufacturing India&rsquo;s
            most trusted welding electrodes. A new article every day.
          </p>
        </div>
      </section>

      {loading || !featured ? (
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="animate-pulse space-y-6">
              <div className="h-64 bg-neutral-100 rounded-2xl" />
              <div className="h-4 bg-neutral-100 rounded w-1/2" />
              <div className="h-4 bg-neutral-100 rounded w-2/3" />
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-white py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <Link
                to={`/insights/${featured.slug}`}
                className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-[#0b0b0d] group block"
              >
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/90 bg-white/10 backdrop-blur px-2.5 py-1 rounded">
                  Featured • {featured.category}
                </div>
              </Link>
              <div>
                <div className="flex items-center gap-4 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {featured.date}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> {featured.read_time}</span>
                </div>
                <h2 className="mt-4 font-display text-3xl lg:text-4xl font-semibold text-[#0b0b0d] leading-[1.1]">
                  {featured.title}
                </h2>
                <p className="mt-5 text-neutral-700 leading-relaxed text-lg">{featured.excerpt}</p>
                <Link
                  to={`/insights/${featured.slug}`}
                  className="mt-8 inline-flex items-center gap-2 font-semibold text-[#0b0b0d] hover:text-amber-600 transition-colors"
                >
                  Read the full article <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {rest.length > 0 && (
        <section className="bg-[#f4f1eb] py-20 border-t border-neutral-200/60">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-600 mb-4">
              — More Articles
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0b0b0d] mb-10">
              Recent posts
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {rest.map((p) => (
                <Link
                  to={`/insights/${p.slug}`}
                  key={p.slug}
                  className="card-lift group bg-white rounded-2xl overflow-hidden border border-neutral-200 flex flex-col md:flex-row"
                >
                  <div className="relative md:w-2/5 aspect-[4/3] md:aspect-auto overflow-hidden bg-[#0b0b0d]">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 md:w-3/5 flex flex-col">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-600">
                      {p.category}
                    </div>
                    <h3 className="mt-2 font-display text-xl font-semibold text-[#0b0b0d] leading-snug group-hover:text-amber-700 transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-neutral-600 leading-relaxed flex-1">{p.excerpt}</p>
                    <div className="mt-4 flex items-center gap-4 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
                      <span className="inline-flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {p.date}</span>
                      <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> {p.read_time}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
      <Footer />
    </div>
  );
}
